import React, { useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Avatar, List, ListItem,
  ListItemAvatar, ListItemText, Divider, useTheme, alpha, useMediaQuery,
} from '@mui/material';
import {
  Cake as CakeIcon,
  People as PeopleIcon,
  Favorite as FavoriteIcon,
  EmojiEvents as TrophyIcon,
  CalendarMonth as CalendarIcon,
  FavoriteBorder as HeartOutlineIcon,
  PersonOff as PersonOffIcon,
  BarChart as BarChartIcon,
  Hub as HubIcon,
} from '@mui/icons-material';
import { Membre, Relation } from '../types';

interface Props {
  membres: Membre[];
  relations: Relation[];
}

const genderColor = (sexe?: string) => {
  if (sexe === 'Homme') return '#3B82F6';
  if (sexe === 'Femme') return '#EC4899';
  return '#6B7280';
};

const getNextOccurrence = (dateStr: string): { days: number; year: number } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  let next = new Date(`${today.getFullYear()}-${mm}-${dd}`);
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = new Date(`${today.getFullYear() + 1}-${mm}-${dd}`);
    next.setHours(0, 0, 0, 0);
  }
  const days = Math.round((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return { days, year: next.getFullYear() };
};

const formatDays = (days: number) => {
  if (days === 0) return "Aujourd'hui !";
  if (days === 1) return 'Demain';
  return `Dans ${days} j.`;
};

const formatShortDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

const calcAge = (naissance: string, refDate?: Date): number => {
  const birth = new Date(naissance);
  const ref = refDate ?? new Date();
  return Math.floor((ref.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
};

const MemberRow: React.FC<{
  membre: Membre;
  subtitle: string;
  chip: string;
  chipHighlight?: boolean;
  faded?: boolean;
}> = ({ membre, subtitle, chip, chipHighlight, faded }) => {
  const theme = useTheme();
  const color = genderColor(membre.sexe);
  const initials = `${membre.prenom?.[0] || ''}${membre.nom?.[0] || ''}`.toUpperCase();
  return (
    <ListItem sx={{ py: 1 }}>
      <ListItemAvatar sx={{ minWidth: 44 }}>
        <Avatar sx={{
          width: 32, height: 32,
          bgcolor: alpha(color, faded ? 0.08 : 0.15),
          color: faded ? 'text.disabled' : color,
          fontWeight: 700, fontSize: '0.72rem',
          filter: faded ? 'grayscale(0.6)' : 'none',
        }}>
          {initials}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={600} fontSize="0.85rem"
            color={faded ? 'text.secondary' : 'text.primary'}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {membre.prenom} {membre.nom}
          </Typography>
        }
        secondary={<Typography variant="caption" color="text.disabled">{subtitle}</Typography>}
      />
      <Chip
        label={chip}
        size="small"
        sx={{
          bgcolor: chipHighlight
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.text.primary, 0.06),
          color: chipHighlight ? 'primary.main' : 'text.secondary',
          fontWeight: 600, fontSize: '0.68rem', height: 20, flexShrink: 0, ml: 1,
        }}
      />
    </ListItem>
  );
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  badge?: string;
  iconColor?: string;
}> = ({ icon, title, badge, iconColor }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      px: 2.5, py: 1.75,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Box sx={{ color: iconColor ?? 'primary.main', display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      {badge && (
        <Chip label={badge} size="small"
          sx={{ ml: 'auto', height: 18, fontSize: '0.65rem', opacity: 0.7 }} />
      )}
    </Box>
  );
};

const EmptyState: React.FC<{ icon: React.ReactNode; message: string }> = ({ icon, message }) => (
  <Box sx={{ p: { xs: 2.5, sm: 3.5 }, textAlign: 'center' }}>
    <Box sx={{ color: 'text.disabled', opacity: 0.35, mb: 1 }}>{icon}</Box>
    <Typography variant="body2" color="text.disabled" fontSize="0.82rem">{message}</Typography>
  </Box>
);

const AGE_BUCKETS = [
  { label: '0 – 17 ans', min: 0, max: 17, color: '#10B981' },
  { label: '18 – 39 ans', min: 18, max: 39, color: '#3B82F6' },
  { label: '40 – 59 ans', min: 40, max: 59, color: '#8B5CF6' },
  { label: '60 – 79 ans', min: 60, max: 79, color: '#F59E0B' },
  { label: '80 ans et +', min: 80, max: Infinity, color: '#EF4444' },
];

const Dashboard: React.FC<Props> = ({ membres, relations }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const stats = useMemo(() => {
    const living = membres.filter(m => !m.date_deces).length;
    const couples = relations.filter(r => r.type_relation === 'conjoint').length;
    const parentRels = relations.filter(r => r.type_relation === 'parent').length;
    return { total: membres.length, living, deceased: membres.length - living, couples, parentRels };
  }, [membres, relations]);

  const upcomingBirthdays = useMemo(() =>
    membres
      .filter(m => m.date_naissance && !m.date_deces)
      .map(m => {
        const { days, year } = getNextOccurrence(m.date_naissance!);
        return { membre: m, days, age: year - new Date(m.date_naissance!).getFullYear() };
      })
      .filter(b => b.days <= 60)
      .sort((a, b) => a.days - b.days),
    [membres]);

  const upcomingDeaths = useMemo(() =>
    membres
      .filter(m => m.date_deces)
      .map(m => {
        const { days, year } = getNextOccurrence(m.date_deces!);
        return { membre: m, days, years: year - new Date(m.date_deces!).getFullYear() };
      })
      .filter(d => d.days <= 30)
      .sort((a, b) => a.days - b.days),
    [membres]);

  const { oldest, youngest, ageSpan } = useMemo(() => {
    const living = membres.filter(m => m.date_naissance && !m.date_deces);
    const sorted = [...living].sort(
      (a, b) => new Date(a.date_naissance!).getTime() - new Date(b.date_naissance!).getTime()
    );
    const oldest = sorted[0];
    const youngest = sorted[sorted.length - 1];
    const ageSpan = oldest && youngest
      ? new Date(youngest.date_naissance!).getFullYear() - new Date(oldest.date_naissance!).getFullYear()
      : 0;
    return { oldest, youngest, ageSpan };
  }, [membres]);

  const extraStats = useMemo(() => {
    const deceased = membres.filter(m => m.date_naissance && m.date_deces);
    const avgLifespan = deceased.length
      ? Math.round(deceased.reduce((sum, m) => {
          const b = new Date(m.date_naissance!).getTime();
          const d = new Date(m.date_deces!).getTime();
          return sum + (d - b) / (365.25 * 24 * 60 * 60 * 1000);
        }, 0) / deceased.length)
      : null;

    const prenomCount = new Map<string, number>();
    membres.forEach(m => prenomCount.set(m.prenom, (prenomCount.get(m.prenom) || 0) + 1));
    let topPrenom = '', topPrenomCount = 0;
    prenomCount.forEach((count, prenom) => { if (count > topPrenomCount) { topPrenom = prenom; topPrenomCount = count; } });

    const nomCount = new Map<string, number>();
    membres.forEach(m => nomCount.set(m.nom, (nomCount.get(m.nom) || 0) + 1));
    let topNom = '', topNomCount = 0;
    nomCount.forEach((count, nom) => { if (count > topNomCount) { topNom = nom; topNomCount = count; } });

    const hommes = membres.filter(m => m.sexe === 'Homme').length;
    const femmes = membres.filter(m => m.sexe === 'Femme').length;
    const unknown = membres.length - hommes - femmes;

    return { avgLifespan, topPrenom, topPrenomCount, topNom, topNomCount, hommes, femmes, unknown };
  }, [membres]);

  const avgAgeLiving = useMemo(() => {
    const living = membres.filter(m => m.date_naissance && !m.date_deces);
    if (!living.length) return null;
    return Math.round(living.reduce((sum, m) => sum + calcAge(m.date_naissance!), 0) / living.length);
  }, [membres]);

  const mostConnected = useMemo(() => {
    if (!membres.length || !relations.length) return null;
    const counts = new Map<number, number>();
    relations.forEach(r => {
      counts.set(r.membre_source, (counts.get(r.membre_source) || 0) + 1);
      counts.set(r.membre_cible, (counts.get(r.membre_cible) || 0) + 1);
    });
    let maxId = -1, maxCount = 0;
    counts.forEach((count, id) => { if (count > maxCount) { maxCount = count; maxId = id; } });
    const m = membres.find(x => x.id === maxId);
    return m ? { membre: m, count: maxCount } : null;
  }, [membres, relations]);

  const ageDistribution = useMemo(() => {
    const living = membres.filter(m => m.date_naissance && !m.date_deces);
    const counts = AGE_BUCKETS.map(b => ({
      ...b,
      count: living.filter(m => {
        const age = calcAge(m.date_naissance!);
        return age >= b.min && age <= b.max;
      }).length,
    }));
    const maxCount = Math.max(...counts.map(c => c.count), 1);
    return counts.map(c => ({ ...c, pct: Math.round((c.count / maxCount) * 100) }));
  }, [membres]);

  const STAT_CARDS = [
    { label: 'Membres', value: stats.total, color: theme.palette.primary.main, icon: <PeopleIcon fontSize="small" /> },
    { label: 'Vivants', value: stats.living, color: '#10B981', icon: <FavoriteIcon fontSize="small" /> },
    { label: 'Décédés', value: stats.deceased, color: '#6B7280', icon: <PersonOffIcon fontSize="small" /> },
    { label: 'Couples', value: stats.couples, color: '#F87171', icon: <HeartOutlineIcon fontSize="small" /> },
    { label: 'Filiations', value: stats.parentRels, color: '#8B5CF6', icon: <TrophyIcon fontSize="small" /> },
    { label: 'Âge moy.', value: avgAgeLiving !== null ? `${avgAgeLiving} ans` : '—', color: '#F59E0B', icon: <CakeIcon fontSize="small" /> },
  ];

  const keyStats = [
    oldest && { label: 'Doyen(ne)', value: `${oldest.prenom} ${oldest.nom}`, sub: `${calcAge(oldest.date_naissance!)} ans` },
    youngest && oldest?.id !== youngest?.id && { label: 'Benjamin(e)', value: `${youngest.prenom} ${youngest.nom}`, sub: calcAge(youngest.date_naissance!) === 0 ? 'Moins d\'1 an' : `${calcAge(youngest.date_naissance!)} ans` },
    ageSpan > 0 && { label: 'Écart générations', value: `${ageSpan} ans`, sub: 'entre doyen et benjamin' },
    { label: 'Membres décédés', value: `${stats.deceased}`, sub: `sur ${stats.total} membres` },
    extraStats.avgLifespan !== null && { label: 'Espérance de vie', value: `${extraStats.avgLifespan} ans`, sub: `sur ${membres.filter(m => m.date_naissance && m.date_deces).length} décédés datés` },
    mostConnected && { label: 'Plus connecté(e)', value: `${mostConnected.membre.prenom} ${mostConnected.membre.nom}`, sub: `${mostConnected.count} liens familiaux` },
    extraStats.topPrenomCount > 1 && { label: 'Prénom le + fréquent', value: extraStats.topPrenom, sub: `${extraStats.topPrenomCount} fois` },
    extraStats.topNomCount > 1 && { label: 'Nom le + fréquent', value: extraStats.topNom, sub: `${extraStats.topNomCount} membres` },
  ].filter(Boolean) as { label: string; value: string; sub?: string }[];

  const hasGenderData = extraStats.hommes > 0 || extraStats.femmes > 0;
  const hasAgeData = ageDistribution.some(b => b.count > 0);

  return (
    <Box sx={{
      flex: 1, overflowY: 'auto',
      bgcolor: isDark ? 'background.default' : '#F9FAF5',
    }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>

        {/* ── Stat cards ── */}
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 1.5, sm: 3 } }}>
          {STAT_CARDS.map(card => (
            <Grid item xs={6} sm={4} md={2} key={card.label}>
              <Paper elevation={0} sx={{
                p: { xs: 1.5, sm: 2 }, borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper', height: '100%',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                  <Box sx={{
                    width: { xs: 30, sm: 38 }, height: { xs: 30, sm: 38 },
                    minWidth: { xs: 30, sm: 38 },
                    borderRadius: 2,
                    bgcolor: alpha(card.color, 0.12), color: card.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {card.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800} lineHeight={1.1}
                      sx={{ fontSize: { xs: '1.1rem', sm: '1.4rem' } }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontSize: { xs: '0.62rem', sm: '0.72rem' }, lineHeight: 1.2, display: 'block' }}>
                      {card.label}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Genre distribution bar ── */}
        {hasGenderData && (
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper', p: { xs: 2, sm: 2.5 }, mb: { xs: 1.5, sm: 2 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Répartition par genre</Typography>
              <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2.5 } }}>
                {extraStats.hommes > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#3B82F6', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {extraStats.hommes} homme{extraStats.hommes > 1 ? 's' : ''}
                      {!isMobile && ` (${Math.round(extraStats.hommes / membres.length * 100)}%)`}
                    </Typography>
                  </Box>
                )}
                {extraStats.femmes > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#EC4899', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {extraStats.femmes} femme{extraStats.femmes > 1 ? 's' : ''}
                      {!isMobile && ` (${Math.round(extraStats.femmes / membres.length * 100)}%)`}
                    </Typography>
                  </Box>
                )}
                {extraStats.unknown > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#9CA3AF', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {extraStats.unknown} non renseigné{extraStats.unknown > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', borderRadius: 99, overflow: 'hidden', height: 10, bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
              {extraStats.hommes > 0 && (
                <Box sx={{
                  width: `${extraStats.hommes / membres.length * 100}%`,
                  bgcolor: '#3B82F6', transition: 'width 0.5s ease',
                }} />
              )}
              {extraStats.femmes > 0 && (
                <Box sx={{
                  width: `${extraStats.femmes / membres.length * 100}%`,
                  bgcolor: '#EC4899', transition: 'width 0.5s ease',
                }} />
              )}
              {extraStats.unknown > 0 && (
                <Box sx={{ flex: 1, bgcolor: '#9CA3AF', opacity: 0.4 }} />
              )}
            </Box>
          </Paper>
        )}

        <Grid container spacing={{ xs: 1.5, sm: 2 }}>

          {/* ── Prochains anniversaires ── */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper', overflow: 'hidden',
            }}>
              <SectionHeader
                icon={<CakeIcon sx={{ fontSize: 18 }} />}
                title="Prochains anniversaires"
                badge="60 jours"
              />
              {upcomingBirthdays.length === 0 ? (
                <EmptyState
                  icon={<CakeIcon sx={{ fontSize: 36 }} />}
                  message="Aucun anniversaire dans les 60 prochains jours"
                />
              ) : (
                <List disablePadding sx={{ maxHeight: { xs: 260, sm: 340 }, overflowY: 'auto' }}>
                  {upcomingBirthdays.map((b, i) => (
                    <React.Fragment key={b.membre.id}>
                      {i > 0 && <Divider variant="inset" component="li" sx={{ ml: '52px' }} />}
                      <MemberRow
                        membre={b.membre}
                        subtitle={`${formatShortDate(b.membre.date_naissance!)} · ${b.age} ans`}
                        chip={formatDays(b.days)}
                        chipHighlight={b.days <= 7}
                      />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* ── En chiffres ── */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper', overflow: 'hidden',
            }}>
              <SectionHeader
                icon={<TrophyIcon sx={{ fontSize: 18 }} />}
                title="En chiffres"
                iconColor="#F59E0B"
              />
              <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                {keyStats.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center', fontSize: '0.82rem' }}>
                    Ajoutez des dates de naissance pour voir les statistiques
                  </Typography>
                ) : (
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {keyStats.map(row => (
                      <Grid item xs={12} sm={6} key={row.label}>
                        <Box sx={{
                          p: { xs: 1.25, sm: 1.5 }, borderRadius: 2,
                          bgcolor: alpha(theme.palette.text.primary, 0.03),
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
                          height: '100%',
                        }}>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 0.3, lineHeight: 1.3 }}>
                            {row.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.value}
                          </Typography>
                          {row.sub && (
                            <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: 'block' }}>
                              {row.sub}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* ── Tranches d'âge ── */}
          {hasAgeData && (
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper', overflow: 'hidden',
              }}>
                <SectionHeader
                  icon={<BarChartIcon sx={{ fontSize: 18 }} />}
                  title="Tranches d'âge (vivants)"
                  iconColor="#8B5CF6"
                />
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                  {ageDistribution.map(bucket => (
                    <Box key={bucket.label} sx={{ mb: 1.75, '&:last-child': { mb: 0 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {bucket.label}
                        </Typography>
                        <Typography variant="caption" fontWeight={700} color={bucket.count > 0 ? 'text.primary' : 'text.disabled'}>
                          {bucket.count} personne{bucket.count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{
                        height: 7, borderRadius: 99,
                        bgcolor: alpha(theme.palette.text.primary, 0.06),
                        overflow: 'hidden',
                      }}>
                        <Box sx={{
                          height: '100%',
                          width: `${bucket.pct}%`,
                          bgcolor: bucket.count > 0 ? bucket.color : 'transparent',
                          borderRadius: 99,
                          transition: 'width 0.5s ease',
                          opacity: isDark ? 0.85 : 1,
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* ── Anniversaires de disparition ── */}
          <Grid item xs={12} md={hasAgeData ? 6 : 12}>
            <Paper elevation={0} sx={{
              borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper', overflow: 'hidden',
            }}>
              <SectionHeader
                icon={<CalendarIcon sx={{ fontSize: 18 }} />}
                title="Anniversaires de disparition"
                badge="30 jours"
                iconColor="text.secondary"
              />
              {upcomingDeaths.length === 0 ? (
                <EmptyState
                  icon={<CalendarIcon sx={{ fontSize: 36 }} />}
                  message="Aucun anniversaire de disparition dans les 30 prochains jours"
                />
              ) : (
                <List disablePadding sx={{ maxHeight: { xs: 240, sm: 280 }, overflowY: 'auto' }}>
                  {upcomingDeaths.map((d, i) => (
                    <React.Fragment key={d.membre.id}>
                      {i > 0 && <Divider variant="inset" component="li" sx={{ ml: '52px' }} />}
                      <MemberRow
                        membre={d.membre}
                        subtitle={`${formatShortDate(d.membre.date_deces!)} · ${d.years} ans`}
                        chip={formatDays(d.days)}
                        faded
                      />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

        </Grid>

        {/* Bottom spacer for mobile safe area */}
        <Box sx={{ height: { xs: 'env(safe-area-inset-bottom, 16px)', sm: 0 }, minHeight: { xs: 16, sm: 0 } }} />
      </Box>
    </Box>
  );
};

export default Dashboard;
